/**
 * Custom Hero Carousel
 * Slides between products with arrows + swipe + keyboard.
 * Each slide is a complete product (image, info, ATC form bound to its variant).
 *
 * Dispatches `custom-pdp:product-change` on `document` whenever the active
 * product changes, so other linked sections (feature cards, OLED detail)
 * can follow along.
 *
 * Event detail: { handle, productId, index, accentColor, accentRgb }
 *
 * Accent color is sourced from (in order):
 *   1. data-accent-color on the slide (set from a schema block override)
 *   2. Auto-extracted from the product image (dominant non-grey color)
 *   3. Theme default (lime green)
 *
 * Slide entrance: on every product change, adds .is-entering to the active
 * slide so CSS can replay a staggered fade-up cascade across its children.
 *
 * Start index: when the carousel collection contains the current PDP's
 * product, the section emits data-start-index="N" so the carousel opens
 * directly on that product instead of always starting at index 0.
 */
(function () {
  'use strict';

  var TRANSITION_MS = 400;
  var SWIPE_THRESHOLD = 50;
  var EVENT_NAME = 'custom-pdp:product-change';
  var DEFAULT_ACCENT = '#b6ff3c';
  var DEFAULT_ACCENT_RGB = '182, 255, 60';

  // ---------- Color extraction utility ----------
  function hexToRgb(hex) {
    if (!hex) return null;
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    if (hex.length !== 6) return null;
    var num = parseInt(hex, 16);
    if (isNaN(num)) return null;
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
  }

  function rgbStringFromArray(arr) {
    return arr[0] + ', ' + arr[1] + ', ' + arr[2];
  }

  function rgbCssFromArray(arr) {
    return 'rgb(' + arr[0] + ', ' + arr[1] + ', ' + arr[2] + ')';
  }

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    var h = 0;
    var s = 0;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = ((b - r) / d + 2);
      else h = ((r - g) / d + 4);
      h *= 60;
    }
    return [h, s, l];
  }

  function extractDominantColor(img) {
    try {
      var canvas = document.createElement('canvas');
      var maxSide = 140;
      var ratio = img.naturalWidth > img.naturalHeight
        ? maxSide / img.naturalWidth
        : maxSide / img.naturalHeight;
      canvas.width = Math.max(1, Math.floor(img.naturalWidth * ratio));
      canvas.height = Math.max(1, Math.floor(img.naturalHeight * ratio));
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

      var HUE_BIN = 12;
      var bins = {};
      for (var i = 0; i < data.length; i += 4) {
        var r = data[i];
        var g = data[i + 1];
        var b = data[i + 2];
        var a = data[i + 3];
        if (a < 128) continue;

        var hsl = rgbToHsl(r, g, b);
        var h = hsl[0], s = hsl[1], l = hsl[2];

        if (l < 0.18 || l > 0.92) continue;
        if (s < 0.22) continue;

        var hueBin = Math.floor(h / HUE_BIN);

        var lightBias = 1 - Math.abs(l - 0.55) * 1.4;
        if (lightBias < 0.2) lightBias = 0.2;
        var w = Math.pow(s, 1.5) * lightBias;

        if (!bins[hueBin]) {
          bins[hueBin] = { weight: 0, r: 0, g: 0, b: 0 };
        }
        bins[hueBin].weight += w;
        bins[hueBin].r += r * w;
        bins[hueBin].g += g * w;
        bins[hueBin].b += b * w;
      }

      var winner = null;
      var winnerWeight = 0;
      for (var key in bins) {
        if (bins[key].weight > winnerWeight) {
          winnerWeight = bins[key].weight;
          winner = bins[key];
        }
      }
      if (!winner || winner.weight === 0) return null;

      var or = Math.round(winner.r / winner.weight);
      var og = Math.round(winner.g / winner.weight);
      var ob = Math.round(winner.b / winner.weight);

      var resHsl = rgbToHsl(or, og, ob);
      var hue = resHsl[0];

      var finalSat = Math.min(1, Math.max(0.92, resHsl[1] * 1.6 + 0.15));

      var finalLight;
      if (resHsl[2] >= 0.55 && resHsl[2] <= 0.72) {
        finalLight = resHsl[2];
      } else if (resHsl[2] < 0.55) {
        finalLight = 0.55 + (resHsl[2] - 0.55) * 0.15;
      } else {
        finalLight = 0.72 + (resHsl[2] - 0.72) * 0.15;
      }

      var rgb = hslToRgb(hue, finalSat, finalLight);
      return rgb;
    } catch (e) {
      return null;
    }
  }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360;
    var c = (1 - Math.abs(2 * l - 1)) * s;
    var x = c * (1 - Math.abs((h / 60) % 2 - 1));
    var m = l - c / 2;
    var r1, g1, b1;
    if (h < 60)       { r1 = c; g1 = x; b1 = 0; }
    else if (h < 120) { r1 = x; g1 = c; b1 = 0; }
    else if (h < 180) { r1 = 0; g1 = c; b1 = x; }
    else if (h < 240) { r1 = 0; g1 = x; b1 = c; }
    else if (h < 300) { r1 = x; g1 = 0; b1 = c; }
    else              { r1 = c; g1 = 0; b1 = x; }
    return [
      Math.round((r1 + m) * 255),
      Math.round((g1 + m) * 255),
      Math.round((b1 + m) * 255)
    ];
  }

  function loadImageForExtraction(src, callback) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      var rgb = extractDominantColor(img);
      callback(rgb);
    };
    img.onerror = function () { callback(null); };
    img.src = src;
  }

  // ---------- Carousel ----------
  function CustomHeroCarousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-hero-track]');
    if (!this.track) return;

    this.prevBtn = root.querySelector('[data-hero-prev]');
    this.nextBtn = root.querySelector('[data-hero-next]');
    this.loop = root.dataset.loop !== 'false';

    this.realSlides = Array.from(this.track.querySelectorAll('.custom-hero__slide'));
    this.total = this.realSlides.length;
    this.isAnimating = false;

    if (this.total === 0) return;

    // Read the desired starting index from the section root. Liquid puts
    // the index of the CURRENT product (in the carousel collection) here,
    // so the carousel opens on whichever product the user clicked from
    // the collection page. Defaults to 0 when missing or out of range.
    var startIdx = parseInt(root.dataset.startIndex, 10);
    if (isNaN(startIdx) || startIdx < 0 || startIdx >= this.total) {
      startIdx = 0;
    }
    this.startIndex = startIdx;

    this.applyLedMask();
    this.bindFeaturePopovers();
    this.preExtractAccentColors();

    if (this.total < 2) {
      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      this.bindQty();
      this.dispatchProductChange(0);
      return;
    }

    if (this.loop) {
      this.addClones();
      // With cloned slides, virtual index 1 = first REAL slide.
      // So real index N lives at virtualIndex N + 1.
      this.virtualIndex = this.startIndex + 1;
    } else {
      this.virtualIndex = this.startIndex;
    }

    // Position the track WITHOUT animation so the user lands directly on
    // the right slide instead of seeing a transition from slide 0.
    this.setPosition(false);

    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.prev(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(); });

    this.bindSwipe();
    this.bindKeyboard();
    this.bindQty();

    this.track.addEventListener('transitionend', function (e) {
      if (e.target !== self.track) return;
      self.handleTransitionEnd();
    });

    window.addEventListener('resize', function () { self.setPosition(false); });

    // Fire the initial product-change event for the starting slide so
    // feature cards, OLED detail, etc. all sync to the right product
    // on page load — not just slide 0.
    this.dispatchProductChange(this.realIndex());
  }

  /**
   * Find the first LED bar image inside the section and expose its URL
   * as --ch-led-mask on the section root. The CSS overlay then masks
   * itself with this URL so the colored hover layer matches the LED's
   * exact silhouette (any shape — strip, wave, segments).
   */
  CustomHeroCarousel.prototype.applyLedMask = function () {
    var img = this.root.querySelector('.custom-hero__feature-led-img');
    if (!img) return;

    var apply = function () {
      var src = img.currentSrc || img.src;
      if (!src) return;
      // url("...") syntax so commas/parens in the path don't break the var
      this.root.style.setProperty('--ch-led-mask', 'url("' + src + '")');
    }.bind(this);

    if (img.complete && img.naturalWidth > 0) {
      apply();
    } else {
      img.addEventListener('load', apply, { once: true });
    }
  };

  CustomHeroCarousel.prototype.realIndex = function () {
    if (!this.loop) return this.virtualIndex;
    if (this.virtualIndex === 0) return this.total - 1;
    if (this.virtualIndex === this.total + 1) return 0;
    return this.virtualIndex - 1;
  };

  CustomHeroCarousel.prototype.preExtractAccentColors = function () {
    var self = this;
    this.realSlides.forEach(function (slide, i) {
      var preset = slide.getAttribute('data-accent-color');
      if (preset) {
        var rgb = hexToRgb(preset);
        if (rgb) {
          slide.setAttribute('data-accent-rgb', rgbStringFromArray(rgb));
        }
        applyAccentToSlide(slide, preset, rgb ? rgbStringFromArray(rgb) : null);
        return;
      }
      var img = slide.querySelector('img.custom-hero__image');
      if (!img || !img.src) return;

      loadImageForExtraction(img.src, function (rgbArr) {
        if (!rgbArr) return;
        var rgbStr = rgbStringFromArray(rgbArr);
        var rgbCss = rgbCssFromArray(rgbArr);
        slide.setAttribute('data-accent-color', rgbCss);
        slide.setAttribute('data-accent-rgb', rgbStr);
        applyAccentToSlide(slide, rgbCss, rgbStr);
        if (i === self.realIndex()) {
          self.dispatchProductChange(i);
        }
      });
    });
  };

  function applyAccentToSlide(slide, color, rgbStr) {
    if (color) slide.style.setProperty('--ch-accent', color);
    if (rgbStr) slide.style.setProperty('--ch-accent-rgb', rgbStr);
  }

  CustomHeroCarousel.prototype.activeRealSlide = function () {
    var i = this.realIndex();
    return this.realSlides[i] || this.realSlides[0];
  };

  CustomHeroCarousel.prototype.dispatchProductChange = function (i) {
    var slide = this.realSlides[i];
    if (!slide) return;

    // ---- Replay entrance animation on the active slide ----
    // Remove the class, force a reflow, then re-add it. Without the reflow,
    // the browser batches the class changes and the animation doesn't replay.
    this.realSlides.forEach(function (s) { s.classList.remove('is-entering'); });
    // eslint-disable-next-line no-unused-expressions
    slide.offsetHeight;
    slide.classList.add('is-entering');

    var accentColor = slide.getAttribute('data-accent-color') || DEFAULT_ACCENT;
    var accentRgb = slide.getAttribute('data-accent-rgb') || DEFAULT_ACCENT_RGB;
    // Reflect active slide's accent on the host section so hovers immediately
    // use the right color even before listeners react.
    this.root.style.setProperty('--ch-accent', accentColor);
    this.root.style.setProperty('--ch-accent-rgb', accentRgb);

    var detail = {
      handle: slide.getAttribute('data-product-handle'),
      productId: slide.getAttribute('data-product-id'),
      index: i,
      accentColor: accentColor,
      accentRgb: accentRgb
    };
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail, bubbles: true }));
  };

  CustomHeroCarousel.prototype.addClones = function () {
    var firstClone = this.realSlides[0].cloneNode(true);
    var lastClone = this.realSlides[this.total - 1].cloneNode(true);
    firstClone.classList.add('is-clone');
    lastClone.classList.add('is-clone');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.setAttribute('data-clone', 'first');
    lastClone.setAttribute('data-clone', 'last');
    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.realSlides[0]);
  };

  CustomHeroCarousel.prototype.setPosition = function (animate) {
    this.track.style.transition = animate
      ? 'transform ' + TRANSITION_MS + 'ms cubic-bezier(0.4, 0, 0.2, 1)'
      : 'none';
    this.track.style.transform = 'translateX(-' + (this.virtualIndex * 100) + '%)';
    if (!animate) {
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetHeight;
    }
  };

  CustomHeroCarousel.prototype.next = function () {
    if (this.isAnimating) return;
    var lastReal = this.loop ? this.total : this.total - 1;
    if (!this.loop && this.virtualIndex >= lastReal) return;
    this.isAnimating = true;
    this.virtualIndex++;
    this.setPosition(true);
    this.dispatchProductChange(this.realIndex());
  };

  CustomHeroCarousel.prototype.prev = function () {
    if (this.isAnimating) return;
    var firstReal = this.loop ? 1 : 0;
    if (!this.loop && this.virtualIndex <= firstReal) return;
    this.isAnimating = true;
    this.virtualIndex--;
    this.setPosition(true);
    this.dispatchProductChange(this.realIndex());
  };

  CustomHeroCarousel.prototype.handleTransitionEnd = function () {
    this.isAnimating = false;
    if (!this.loop) return;
    if (this.virtualIndex === 0) {
      this.virtualIndex = this.total;
      this.setPosition(false);
    } else if (this.virtualIndex === this.total + 1) {
      this.virtualIndex = 1;
      this.setPosition(false);
    }
  };

  CustomHeroCarousel.prototype.bindSwipe = function () {
    var self = this;
    var startX = 0;
    var startY = 0;
    var isTouching = false;

    this.root.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isTouching = true;
    }, { passive: true });

    this.root.addEventListener('touchend', function (e) {
      if (!isTouching) return;
      var endX = e.changedTouches[0].clientX;
      var endY = e.changedTouches[0].clientY;
      var dx = endX - startX;
      var dy = endY - startY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.2) {
        if (dx > 0) self.prev();
        else self.next();
      }
      isTouching = false;
    }, { passive: true });
  };

  CustomHeroCarousel.prototype.bindKeyboard = function () {
    var self = this;
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        self.prev();
      } else if (e.key === 'ArrowRight') {
        self.next();
      }
    });
  };

  CustomHeroCarousel.prototype.bindQty = function () {
    this.root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-qty-step]');
      if (!btn) return;
      var form = btn.closest('form');
      if (!form) return;
      var input = form.querySelector('.custom-hero__qty-input');
      if (!input) return;
      var step = parseInt(btn.getAttribute('data-qty-step'), 10) || 0;
      var current = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, current + step);
    });
  };

  CustomHeroCarousel.prototype.bindFeaturePopovers = function () {
    // Popovers are hover/focus-driven via CSS. Intercept clicks on the
    // button so a tap doesn't keep the popover stuck open via :focus-within.
    this.root.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-feature-trigger]');
      if (!trigger) return;
      e.preventDefault();
      trigger.blur();
    });
  };

  function init() {
    var roots = document.querySelectorAll('[data-custom-hero]');
    for (var i = 0; i < roots.length; i++) {
      var el = roots[i];
      if (el.__customHeroInited) continue;
      el.__customHeroInited = true;
      new CustomHeroCarousel(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();